import React, { useEffect, useState, useMemo } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getAdLink } from '../utils/adUtils';
import Spinner from '../components/Spinner';
import AdBanner from '../components/AdBanner';
import { adConfig } from '../config/adConfig';
import './Home.css';
import './AdsPage.css';

const AdsPage = ({ type }) => {
    const [searchParams] = useSearchParams();
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFilterExpanded, setIsFilterExpanded] = useState(false);

    // Filter states — pre-filled from URL query params (passed from Home)
    const [filters, setFilters] = useState({
        search: searchParams.get('search') || '',
        location: searchParams.get('location') || '',
        condition: searchParams.get('condition') || '',
        vehicleType: searchParams.get('vehicleType') || '',
        minPrice: '',
        maxPrice: '',
        minYear: '',
        maxYear: '',
        transmission: ''
    });

    const vehicleTypes = ["Car", "Van", "SUV", "Jeep", "Motorbike", "Three Wheeler", "Truck", "Bus", "Lorry", "Bicycle", "Parts & Accessories"];
    const conditions = ["New", "Used", "Reconditioned"];
    const transmissions = ["Manual", "Automatic", "Tiptronic", "Other"];

    useEffect(() => {
        const loadAds = async () => {
            setLoading(true);
            try {
                const adsRef = collection(db, "ads");
                const q = query(adsRef);
                const querySnapshot = await getDocs(q);
                const totalAdsCount = querySnapshot.size;

                let fetchedAds = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })).filter(ad => totalAdsCount >= 50 || ad.status !== 'pending');

                // Initial filtering based on category
                if (type === 'rent') {
                    fetchedAds = fetchedAds.filter(ad => ad.category === 'Rent' || ad.category === 'Rent Vehicle');
                } else if (type === 'parts') {
                    fetchedAds = fetchedAds.filter(ad => ad.category === 'Parts' || ad.category === 'Sell Parts');
                } else if (type === 'service') {
                    fetchedAds = fetchedAds.filter(ad => ad.category === 'Service Station');
                } else {
                    // Default to 'Buy' - show Sell and older vehicle categories
                    fetchedAds = fetchedAds.filter(ad =>
                        ad.category === 'Sell' ||
                        ad.category === 'Sell Vehicle' ||
                        ad.category === 'Sell MotorBike' ||
                        ad.category === 'Sell Bicycle' ||
                        (!ad.category) // Fallback for very old ads
                    );
                }

                // Sort by date desc
                fetchedAds.sort((a, b) => {
                    const dateA = a.createdAt?.seconds || 0;
                    const dateB = b.createdAt?.seconds || 0;
                    return dateB - dateA;
                });

                setAds(fetchedAds);
            } catch (error) {
                console.error("Failed to load ads:", error);
            } finally {
                setLoading(false);
            }
        };

        loadAds();
    }, [type]);

    // Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const resetFilters = () => {
        setFilters({
            search: '',
            location: '',
            condition: '',
            vehicleType: '',
            minPrice: '',
            maxPrice: '',
            minYear: '',
            maxYear: '',
            transmission: ''
        });
    };

    // Filtered ads derived state
    const filteredAds = useMemo(() => {
        return ads.filter(ad => {
            const matchesSearch = !filters.search ||
                ad.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
                ad.vehicleMake?.toLowerCase().includes(filters.search.toLowerCase()) ||
                ad.model?.toLowerCase().includes(filters.search.toLowerCase());

            const matchesLocation = !filters.location ||
                ad.location?.toLowerCase().includes(filters.location.toLowerCase());

            const matchesCondition = !filters.condition ||
                ad.vehicleCondition === filters.condition;

            const matchesType = !filters.vehicleType ||
                ad.vehicleType === filters.vehicleType;

            const adPrice = parseFloat(ad.price?.toString().replace(/[^0-9.]/g, '')) || 0;
            const matchesMinPrice = !filters.minPrice || adPrice >= parseFloat(filters.minPrice);
            const matchesMaxPrice = !filters.maxPrice || adPrice <= parseFloat(filters.maxPrice);

            const adYear = parseInt(ad.manufacturedYear) || 0;
            const matchesMinYear = !filters.minYear || adYear >= parseInt(filters.minYear);
            const matchesMaxYear = !filters.maxYear || adYear <= parseInt(filters.maxYear);

            const matchesTransmission = !filters.transmission || ad.transmission === filters.transmission;

            return matchesSearch && matchesLocation && matchesCondition && matchesType &&
                matchesMinPrice && matchesMaxPrice && matchesMinYear && matchesMaxYear &&
                matchesTransmission;
        });
    }, [ads, filters]);

    const pageTitle = {
        'rent': 'Rent a Vehicle',
        'parts': 'Parts & Accessories',
        'service': 'Service Stations',
        'buy': 'Buy a Vehicle'
    }[type] || 'Browse Ads';

    // Toggle this condition to show or hide the ad banner (using global config)
    const showAdBanner = adConfig.showAdBanner;

    useEffect(() => {
        if (isFilterExpanded) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isFilterExpanded]);

    return (
        <div className="ads-page-container">
            <Helmet>
                <title>{`${pageTitle} in Sri Lanka | RiyaMaga — riyamaga.site`}</title>
                <meta name="description" content={`Browse the latest ${pageTitle} listings in Sri Lanka. Find the best deals on cars, bikes, parts and services on RiyaMaga. Updated daily.`} />
                <meta name="keywords" content={`${pageTitle} Sri Lanka, ${type} vehicle Sri Lanka, cheap ${pageTitle}, RiyaMaga, riyamaga.site`} />
                <link rel="canonical" href={`https://riyamaga.site/${type}`} />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={`${pageTitle} in Sri Lanka | RiyaMaga`} />
                <meta property="og:description" content={`Explore ${pageTitle} in Sri Lanka — only on RiyaMaga, the trusted vehicle marketplace.`} />
                <meta property="og:url" content={`https://riyamaga.site/${type}`} />
                <meta property="og:image" content="https://riyamaga.site/logo192.png" />
                <meta property="og:site_name" content="RiyaMaga" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={`${pageTitle} in Sri Lanka | RiyaMaga`} />
                <meta name="twitter:description" content={`Find ${pageTitle} in Sri Lanka on RiyaMaga.`} />
                <meta name="twitter:image" content="https://riyamaga.site/logo192.png" />
            </Helmet>

            {/* Ad Banner between Nav Header and Page Title */}
            {showAdBanner && <AdBanner />}

            <div className="ads-page-header">
                <h2 className="section-title">{pageTitle}</h2>
                <div className="ads-header-actions">
                    <div className="ads-search-bar">
                        <i className="fa-solid fa-magnifying-glass search-icon"></i>
                        <input
                            type="text"
                            name="search"
                            placeholder="Search make, model or title..."
                            value={filters.search}
                            onChange={handleFilterChange}
                        />
                        {filters.search && (
                            <button
                                className="btn-clear-search"
                                onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        )}
                    </div>
                    <button className="btn-filter-trigger" onClick={() => setIsFilterExpanded(true)}>
                        <i className="fa-solid fa-sliders"></i>
                        <span>Filters</span>
                    </button>
                </div>
            </div>

            {/* Popup Filter Panel */}
            {isFilterExpanded && (
                <div className="filter-popup-overlay" onClick={() => setIsFilterExpanded(false)}>
                    <div className="filter-popup-container" onClick={(e) => e.stopPropagation()}>
                        <div className="filter-popup-header">
                            <div className="filter-popup-title">
                                <i className="fa-solid fa-sliders"></i>
                                <span>Refine Search</span>
                            </div>
                            <button className="btn-close-popup" onClick={() => setIsFilterExpanded(false)}>
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <div className="filter-popup-body">
                            <div className="filter-grid">
                                <div className="filter-group">
                                    <label>Search</label>
                                    <input
                                        type="text"
                                        name="search"
                                        value={filters.search}
                                        onChange={handleFilterChange}
                                        placeholder="e.g. Toyota Aqua"
                                    />
                                </div>
                                <div className="filter-group">
                                    <label>Location</label>
                                    <input
                                        type="text"
                                        name="location"
                                        value={filters.location}
                                        onChange={handleFilterChange}
                                        placeholder="e.g. Colombo"
                                    />
                                </div>
                                <div className="filter-group">
                                    <label>Type</label>
                                    <select name="vehicleType" value={filters.vehicleType} onChange={handleFilterChange}>
                                        <option value="">All Types</option>
                                        {vehicleTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Transmission</label>
                                    <select name="transmission" value={filters.transmission} onChange={handleFilterChange}>
                                        <option value="">All</option>
                                        {transmissions.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Condition</label>
                                    <select name="condition" value={filters.condition} onChange={handleFilterChange}>
                                        <option value="">All Conditions</option>
                                        {conditions.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="filter-group-row">
                                    <div className="filter-group">
                                        <label>Min Price</label>
                                        <input type="number" name="minPrice" value={filters.minPrice} onChange={handleFilterChange} placeholder="Min" />
                                    </div>
                                    <div className="filter-group">
                                        <label>Max Price</label>
                                        <input type="number" name="maxPrice" value={filters.maxPrice} onChange={handleFilterChange} placeholder="Max" />
                                    </div>
                                </div>
                                <div className="filter-group-row">
                                    <div className="filter-group">
                                        <label>Min Year</label>
                                        <input type="number" name="minYear" value={filters.minYear} onChange={handleFilterChange} placeholder="From" />
                                    </div>
                                    <div className="filter-group">
                                        <label>Max Year</label>
                                        <input type="number" name="maxYear" value={filters.maxYear} onChange={handleFilterChange} placeholder="To" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="filter-popup-footer">
                            <button className="btn-reset-filters" onClick={resetFilters}>Reset All</button>
                            <button className="btn-show-results" onClick={() => setIsFilterExpanded(false)}>
                                Show {filteredAds.length} Results
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ads Grid */}
            <section className="home-section">
                {loading ? (
                    <Spinner message="Loading listings..." />
                ) : (
                    <div className="ads-grid">
                        {filteredAds.length > 0 ? (
                            filteredAds.map((ad, index) => (
                                <React.Fragment key={ad.id}>
                                    <Link to={getAdLink(ad)} className="ad-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                                        <div className="ad-body">
                                            <img
                                                src={ad.image || 'https://placehold.co/600x400/1e293b/ffffff?text=No+Image'}
                                                alt={ad.title}
                                                className="ad-image"
                                            />
                                            <div className="ad-info">
                                                <div className="ad-title-row">
                                                    <h3 className="ad-title">
                                                        {ad.title || `${ad.vehicleMake || ''} ${ad.model || ''}`.trim() || 'Vehicle Ad'}
                                                        {ad.manufacturedYear && <span className="ad-title-year"> ({ad.manufacturedYear})</span>}
                                                    </h3>
                                                    {ad.vehicleCondition && (
                                                        <span className="ad-condition">{ad.vehicleCondition}</span>
                                                    )}
                                                </div>
                                                <p className="ad-location">{ad.location}</p>
                                                <p className="ad-price">{ad.price}</p>
                                                <div className="ad-mileage-row">
                                                    <p className="ad-mileage">{ad.mileage ? `${ad.mileage} (km)` : ''}</p>
                                                    <p className="ad-date">
                                                        {ad.createdAt?.seconds ? new Date(ad.createdAt.seconds * 1000).toLocaleDateString() : 'Recently'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </React.Fragment>
                            ))
                        ) : (
                            <div style={{ textAlign: 'center', width: '100%', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                                <p>No results found for your filters.</p>
                                <button onClick={resetFilters} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', textDecoration: 'underline', cursor: 'pointer', marginTop: '1rem' }}>
                                    Clear all filters
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
};

export default AdsPage;
